/* Chroma extraction and chord template matching. */
import { getFFT } from './fft';
import { hann, type Samples, work } from './math';

export const QUALITIES = [
  '',
  'm',
  '7',
  'maj7',
  'm7',
  'dim',
  'aug',
  'sus2',
  'sus4',
  'm7b5',
  '6',
  'm6',
  '5',
  'dim7',
  'add9',
] as const;
export type ChordQuality = (typeof QUALITIES)[number];

/** Chord intervals in semitones above the root. */
export const QUAL: Record<ChordQuality, readonly number[]> = {
  '': [0, 4, 7],
  m: [0, 3, 7],
  '7': [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  m7b5: [0, 3, 6, 10],
  '6': [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  '5': [0, 7],
  dim7: [0, 3, 6, 9],
  add9: [0, 2, 4, 7],
};

export const isQuality = (q: string): q is ChordQuality => Object.hasOwn(QUAL, q);

/** A chord identified by pitch class (0 = C) and quality. */
export interface ChordRef {
  root: number;
  q: ChordQuality;
}

export interface ChromaOptions {
  a4?: number;
  fmin?: number;
  fmax?: number;
  relThr?: number;
  suppress?: number;
  gamma?: number;
}

export interface ChromaResult {
  chroma: Float64Array;
  /** Pitch class of the lowest strong peak, -1 when none. */
  bass: number;
  energy: number;
  peaks: { f: number; amp: number }[];
}

/** `buf` is a power-of-two window (~0.35 s). */
export function chroma(buf: Samples, sr: number, opts: ChromaOptions = {}): ChromaResult {
  const a4 = opts.a4 || 440;
  const fmin = opts.fmin || 70;
  const fmax = opts.fmax || 1800;
  const N = buf.length;
  const re = work('cre', N);
  const im = work('cim', N);
  const win = hann(N);
  let mean = 0;
  for (let i = 0; i < N; i++) mean += buf[i];
  mean /= N;
  for (let i = 0; i < N; i++) {
    re[i] = (buf[i] - mean) * win[i];
    im[i] = 0;
  }
  getFFT(N)(re, im, false);
  const kmin = Math.max(2, Math.floor((fmin * N) / sr));
  const kmax = Math.min((N >> 1) - 2, Math.ceil((fmax * N) / sr));
  const mag = work('cmag', N >> 1);
  let maxMag = 0;
  let energy = 0;
  for (let k = kmin - 1; k <= kmax + 1; k++) {
    const v = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
    mag[k] = v;
    if (k >= kmin && k <= kmax) {
      energy += v * v;
      if (v > maxMag) maxMag = v;
    }
  }
  const out = new Float64Array(12);
  if (maxMag <= 0) return { chroma: out, bass: -1, energy: 0, peaks: [] };
  const thr = maxMag * (opts.relThr || 0.025);
  const peaks: { f: number; amp: number }[] = [];
  for (let k = kmin; k <= kmax; k++) {
    const v = mag[k];
    if (v < thr || v <= mag[k - 1] || v < mag[k + 1]) continue;
    const a = Math.log(mag[k - 1] + 1e-12);
    const b = Math.log(v + 1e-12);
    const c = Math.log(mag[k + 1] + 1e-12);
    const den = a - 2 * b + c;
    let p = 0;
    if (den < 0) p = (0.5 * (a - c)) / den;
    if (p > 0.5 || p < -0.5) p = 0;
    peaks.push({ f: ((k + p) * sr) / N, amp: Math.exp(b - 0.25 * (a - c) * p) });
  }
  // Harmonic suppression: a peak on the 2nd..6th harmonic of a louder-or-similar lower peak
  // mostly repeats information, so it counts less.
  const supp = opts.suppress == null ? 0.6 : opts.suppress;
  const gamma = opts.gamma || 0.4;
  let bass = -1;
  let bassF = Infinity;
  for (let i = 0; i < peaks.length; i++) {
    const pk = peaks[i] as { f: number; amp: number };
    let w = (pk.amp / maxMag) ** gamma;
    if (supp > 0) {
      for (let j = 0; j < i; j++) {
        const lo = peaks[j] as { f: number; amp: number };
        if (lo.amp < pk.amp * 0.5) continue;
        const ratio = pk.f / lo.f;
        const n = Math.round(ratio);
        if (n >= 2 && n <= 6 && Math.abs(ratio - n) < 0.03 * n) {
          w *= supp;
          break;
        }
      }
    }
    if (pk.f > 1000) w *= 1000 / pk.f;
    const mf = 69 + 12 * Math.log2(pk.f / a4);
    const r = Math.round(mf);
    const dev = mf - r;
    w *= Math.exp(-(dev / 0.25) * (dev / 0.25));
    out[((r % 12) + 12) % 12] += w;
    if (pk.f < 330 && pk.amp >= maxMag * 0.12 && pk.f < bassF) {
      bassF = pk.f;
      bass = ((r % 12) + 12) % 12;
    }
  }
  return { chroma: out, bass, energy: Math.sqrt(energy), peaks };
}

const tplCache = new Map<string, Float64Array>();

export function chordTemplate(root: number, q: ChordQuality, h3 = 0.3, h5 = 0.1): Float64Array {
  const key = `${root}|${q}|${h3}|${h5}`;
  const cached = tplCache.get(key);
  if (cached) return cached;
  const t = new Float64Array(12);
  for (const iv of QUAL[q]) {
    const p = (root + iv) % 12;
    t[p] += 1;
    t[(p + 7) % 12] += h3;
    t[(p + 4) % 12] += h5;
  }
  let n = 0;
  for (let i = 0; i < 12; i++) n += t[i] * t[i];
  n = Math.sqrt(n);
  for (let i = 0; i < 12; i++) t[i] /= n;
  tplCache.set(key, t);
  return t;
}

/* One note is not a chord, although its harmonics land on the fifth (3rd and 6th harmonics), the third
   (5th) and the seventh (7th): a sung "aaa" otherwise reads as a major or dominant 7th chord. This is the
   chroma of one note, root first, measured on sung notes (five vowels, with and without vibrato) and
   plucked strings from G2 to G5. */
const NOTE_PROFILE = (() => {
  const p = [0.9, 0, 0.05, 0, 0.16, 0, 0, 0.4, 0, 0, 0.06, 0];
  const n = Math.hypot(...p);
  return p.map((x) => x / n);
})();

/** True when the harmonics of a single note explain the chroma better than any chord of `vocab` (by
    `margin`): someone sings or plays one note. In dsp.test.ts about 9 in 10 single-note frames count as
    one note, and about 1 strummed-chord frame in 100 does. */
export function isSingleNote(chromaVec: Float64Array, vocab: readonly ChordRef[], margin = 0.02): boolean {
  let n = 0;
  for (let i = 0; i < 12; i++) n += chromaVec[i] * chromaVec[i];
  n = Math.sqrt(n) || 1;
  let note = 0;
  for (let r = 0; r < 12; r++) {
    let s = 0;
    for (let i = 0; i < 12; i++) s += (chromaVec[(i + r) % 12] / n) * (NOTE_PROFILE[i] as number);
    note = Math.max(note, s);
  }
  let chord = 0;
  for (const v of vocab) {
    const t = chordTemplate(v.root, v.q);
    let s = 0;
    for (let i = 0; i < 12; i++) s += (chromaVec[i] / n) * t[i];
    chord = Math.max(chord, s);
  }
  return note - chord > margin;
}

export interface MatchOptions {
  h3?: number;
  h5?: number;
  bassBonus?: number;
  complexPenalty?: number;
}

export interface ChordMatch extends ChordRef {
  score: number;
}

/** Ranks every chord of `vocab` against a chroma vector, best first. */
export function matchChord(
  chromaVec: Float64Array,
  bass: number,
  vocab: readonly ChordRef[],
  opts: MatchOptions = {},
): ChordMatch[] {
  let n = 0;
  for (let i = 0; i < 12; i++) n += chromaVec[i] * chromaVec[i];
  n = Math.sqrt(n) || 1;
  const out: ChordMatch[] = [];
  for (const v of vocab) {
    const t = chordTemplate(v.root, v.q, opts.h3, opts.h5);
    let s = 0;
    for (let i = 0; i < 12; i++) s += (chromaVec[i] / n) * t[i];
    if (bass === v.root) s += opts.bassBonus == null ? 0.03 : opts.bassBonus;
    const size = QUAL[v.q].length;
    if (size > 3) s -= opts.complexPenalty == null ? 0.005 : opts.complexPenalty;
    if (size < 3) s -= 0.03;
    out.push({ root: v.root, q: v.q, score: s });
  }
  out.sort((a, b) => b.score - a.score);
  return out;
}
